/** Currency metadata and site-currency helpers (shared by CurrencyContext and the currency components). */
import { SITE, BASE_CURRENCY } from '@/site';

// Currency metadata with flags, names, and symbols
export const CURRENCY_DATA: Record<string, {
  name: string;
  country: string;
  flag: string;
  symbol: string;
  decimals: number;
}> = {
  // Yen and yuan share ¥: the yuan is always CN¥, the yen is JP¥ next to a yuan base.
  JPY: { name: 'Japanese Yen', country: 'Japan', flag: '🇯🇵', symbol: BASE_CURRENCY === 'CNY' ? 'JP¥' : '¥', decimals: 0 },
  USD: { name: 'US Dollar', country: 'United States', flag: '🇺🇸', symbol: '$', decimals: 2 },
  EUR: { name: 'Euro', country: 'Eurozone', flag: '🇪🇺', symbol: '€', decimals: 2 },
  GBP: { name: 'British Pound', country: 'United Kingdom', flag: '🇬🇧', symbol: '£', decimals: 2 },
  AUD: { name: 'Australian Dollar', country: 'Australia', flag: '🇦🇺', symbol: 'A$', decimals: 2 },
  CAD: { name: 'Canadian Dollar', country: 'Canada', flag: '🇨🇦', symbol: 'C$', decimals: 2 },
  CNY: { name: 'Chinese Yuan', country: 'China', flag: '🇨🇳', symbol: 'CN¥', decimals: 2 },
  HKD: { name: 'Hong Kong Dollar', country: 'Hong Kong', flag: '🇭🇰', symbol: 'HK$', decimals: 2 },
  SGD: { name: 'Singapore Dollar', country: 'Singapore', flag: '🇸🇬', symbol: 'S$', decimals: 2 },
  KRW: { name: 'South Korean Won', country: 'South Korea', flag: '🇰🇷', symbol: '₩', decimals: 0 },
  TWD: { name: 'Taiwan Dollar', country: 'Taiwan', flag: '🇹🇼', symbol: 'NT$', decimals: 0 },
  CHF: { name: 'Swiss Franc', country: 'Switzerland', flag: '🇨🇭', symbol: 'CHF', decimals: 2 },
  NZD: { name: 'New Zealand Dollar', country: 'New Zealand', flag: '🇳🇿', symbol: 'NZ$', decimals: 2 },
  SEK: { name: 'Swedish Krona', country: 'Sweden', flag: '🇸🇪', symbol: 'kr', decimals: 2 },
  NOK: { name: 'Norwegian Krone', country: 'Norway', flag: '🇳🇴', symbol: 'kr', decimals: 2 },
  DKK: { name: 'Danish Krone', country: 'Denmark', flag: '🇩🇰', symbol: 'kr', decimals: 2 },
  MXN: { name: 'Mexican Peso', country: 'Mexico', flag: '🇲🇽', symbol: '$', decimals: 2 },
  INR: { name: 'Indian Rupee', country: 'India', flag: '🇮🇳', symbol: '₹', decimals: 2 },
  THB: { name: 'Thai Baht', country: 'Thailand', flag: '🇹🇭', symbol: '฿', decimals: 2 },
  MYR: { name: 'Malaysian Ringgit', country: 'Malaysia', flag: '🇲🇾', symbol: 'RM', decimals: 2 },
  IDR: { name: 'Indonesian Rupiah', country: 'Indonesia', flag: '🇮🇩', symbol: 'Rp', decimals: 0 },
  PHP: { name: 'Philippine Peso', country: 'Philippines', flag: '🇵🇭', symbol: '₱', decimals: 2 },
  ZAR: { name: 'South African Rand', country: 'South Africa', flag: '🇿🇦', symbol: 'R', decimals: 2 },
  BRL: { name: 'Brazilian Real', country: 'Brazil', flag: '🇧🇷', symbol: 'R$', decimals: 2 },
  PLN: { name: 'Polish Zloty', country: 'Poland', flag: '🇵🇱', symbol: 'zł', decimals: 2 },
  CZK: { name: 'Czech Koruna', country: 'Czech Republic', flag: '🇨🇿', symbol: 'Kč', decimals: 2 },
  HUF: { name: 'Hungarian Forint', country: 'Hungary', flag: '🇭🇺', symbol: 'Ft', decimals: 0 },
  TRY: { name: 'Turkish Lira', country: 'Turkey', flag: '🇹🇷', symbol: '₺', decimals: 2 },
  ILS: { name: 'Israeli Shekel', country: 'Israel', flag: '🇮🇱', symbol: '₪', decimals: 2 },
  RON: { name: 'Romanian Leu', country: 'Romania', flag: '🇷🇴', symbol: 'lei', decimals: 2 },
  BGN: { name: 'Bulgarian Lev', country: 'Bulgaria', flag: '🇧🇬', symbol: 'лв', decimals: 2 },
  ISK: { name: 'Icelandic Króna', country: 'Iceland', flag: '🇮🇸', symbol: 'kr', decimals: 0 },
};

// The site's own currency is shown the way the site data stores it (e.g. whole won / yuan).
if (CURRENCY_DATA[BASE_CURRENCY]) {
  CURRENCY_DATA[BASE_CURRENCY] = { ...CURRENCY_DATA[BASE_CURRENCY], decimals: SITE.currency.decimals };
}

/** Symbol for the site's own currency (¥ on Japan, ₩ on Korea, CN¥ on China). */
export const BASE_SYMBOL = CURRENCY_DATA[BASE_CURRENCY]?.symbol ?? SITE.currency.symbol;

/** An amount in the site currency, e.g. "¥5,000", "₩65,000", "CN¥300". */
export function formatBaseAmount(amount: number): string {
  const decimals = SITE.currency.decimals;
  return `${BASE_SYMBOL}${amount.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

/** Display name of the country/area a currency belongs to, in the UI language. */
export function currencyCountryName(code: string, language: string): string {
  const data = CURRENCY_DATA[code];
  const fallback = data?.country ?? code;
  if (language === 'en') return fallback;
  try {
    const locale = language === 'zh' ? 'zh-CN' : language;
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code.slice(0, 2)) ?? fallback;
  } catch {
    return fallback;
  }
}

// Priority order for common tourist currencies
export const PRIORITY_CURRENCIES = [
  BASE_CURRENCY,
  ...['JPY', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'CNY', 'HKD', 'SGD', 'KRW', 'TWD', 'THB'].filter((c) => c !== BASE_CURRENCY),
];
